/*
 * Toplu Abonelik Yenile Modalı
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Checkbox,
  Spinner,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  useToast,
  Box,
  Alert,
  AlertIcon,
  Select,
  FormControl,
  FormLabel,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import api from "utils/api";

export default function TopluAbonelikYenileModal({
  isOpen,
  onClose,
  firmaId,
  preselectDeviceId,
  onSuccess,
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [aySayisi, setAySayisi] = useState("12");
  const [simAskOpen, setSimAskOpen] = useState(false);
  const simCancelRef = useRef(null);

  const bosaSelectedCount = useMemo(() => {
    return selectedIds.filter((id) => {
      const row = items.find((i) => i.cihaz_id === id);
      return row?.sim_bosa_cikan;
    }).length;
  }, [selectedIds, items]);

  useEffect(() => {
    if (!isOpen || !firmaId) return;
    setLoading(true);
    setSelectedIds(preselectDeviceId ? [preselectDeviceId] : []);
    api
      .get(`/api/abonelikler/firma/${firmaId}/devices`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Araçlar yüklenemedi");
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        toast({
          title: "Hata",
          description: err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => setLoading(false));
  }, [isOpen, firmaId, preselectDeviceId, toast]);

  useEffect(() => {
    if (!isOpen) setSimAskOpen(false);
  }, [isOpen]);

  const allChecked = useMemo(
    () => items.length > 0 && selectedIds.length === items.length,
    [items, selectedIds]
  );

  const toggleAll = () => {
    if (allChecked) setSelectedIds([]);
    else setSelectedIds(items.map((i) => i.cihaz_id));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /** @param {boolean|undefined} restoreBosaCikanSim true/false = diyalog; undefined = alan gönderme (backend varsayılan) */
  const runBulkRenew = async (restoreBosaCikanSim) => {
    const ayInt = parseInt(aySayisi, 10);
    try {
      setSaving(true);
      const body = {
        firma_id: firmaId,
        device_ids: selectedIds,
        ay_sayisi: ayInt,
      };
      if (restoreBosaCikanSim === true) {
        body.restore_bosa_cikan_sim = true;
      } else if (restoreBosaCikanSim === false) {
        body.restore_bosa_cikan_sim = false;
      }
      const res = await api.post("/api/abonelikler/bulk-renew", body);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Toplu yenileme başarısız");
      toast({
        title: "Toplu yenileme tamamlandı",
        description: `${data.updated?.length || selectedIds.length} cihaz güncellendi`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast({
        title: "Hata",
        description: err.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!selectedIds.length) {
      toast({ title: "En az bir araç seçin", status: "warning", duration: 3000 });
      return;
    }
    const ayInt = parseInt(aySayisi, 10);
    if (!ayInt || ![1, 3, 12, 24].includes(ayInt)) {
      toast({ title: "Lütfen 1 / 3 / 12 / 24 ay seçeneklerinden birini seçin", status: "warning", duration: 3000 });
      return;
    }
    if (bosaSelectedCount > 0) {
      setSimAskOpen(true);
      return;
    }
    runBulkRenew();
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered motionPreset="slideInBottom">
      <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" boxShadow="xl" m={4} maxH="90vh" overflow="hidden" bg="white" p={1}>
        {/* Header */}
        <Box bg="white" p={4} borderBottom="1px solid" borderColor="gray.200">
          <ModalCloseButton size="sm" />
          <VStack spacing={1} align="start">
            <Text fontSize="lg" fontWeight="medium" color="gray.800">
              Toplu Abonelik Yenile
            </Text>
            <Text fontSize="xs" color="gray.600">
              Seçili araçlar için toplu yenileme
            </Text>
          </VStack>
        </Box>
        <ModalBody px={6} py={4} maxH="500px" overflowY="auto">
          <VStack align="stretch" spacing={4}>
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Bu listede seçtiğiniz araçlar için abonelik bitiş tarihi topluca güncellenecektir
                (Traccar + Admin).
              </Text>
            </Alert>

            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Süre (Ay)</FormLabel>
              <Select
                variant="flushed"
                size="sm"
                value={aySayisi}
                onChange={(e) => setAySayisi(e.target.value)}
              >
                <option value="1">1 Ay</option>
                <option value="3">3 Ay</option>
                <option value="12">12 Ay</option>
                <option value="24">24 Ay</option>
              </Select>
            </FormControl>

            {loading ? (
              <HStack>
                <Spinner size="sm" />
                <Text fontSize="sm">Araçlar yükleniyor...</Text>
              </HStack>
            ) : items.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                Bu firmaya ait araç bulunamadı.
              </Text>
            ) : (
              <Box borderWidth="1px" borderRadius="md" overflow="auto" maxH="360px">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>
                        <Checkbox
                          isChecked={allChecked}
                          onChange={toggleAll}
                          colorScheme="blue"
                        />
                      </Th>
                      <Th>Plaka</Th>
                      <Th>IMEI</Th>
                      <Th>SIM</Th>
                      <Th>Satış Tarihi</Th>
                      <Th>Bitiş Tarihi</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {items.map((row) => {
                      const selected = selectedIds.includes(row.cihaz_id);
                      return (
                        <Tr key={row.cihaz_id} bg={selected ? "blue.50" : "inherit"}>
                          <Td>
                            <Checkbox
                              isChecked={selected}
                              onChange={() => toggleOne(row.cihaz_id)}
                              colorScheme="blue"
                            />
                          </Td>
                          <Td>{row.plaka || "-"}</Td>
                          <Td>{row.imei || "-"}</Td>
                          <Td>
                            {row.sim_bosa_cikan ? (
                              <Badge colorScheme="orange" fontSize="10px">
                                Boşa çıkan
                              </Badge>
                            ) : (
                              <Text fontSize="xs" color="gray.500">
                                —
                              </Text>
                            )}
                          </Td>
                          <Td>
                            {row.satis_tarihi
                              ? new Date(row.satis_tarihi).toLocaleDateString("tr-TR")
                              : "-"}
                          </Td>
                          <Td>
                            {row.dongu_tarihi
                              ? new Date(row.dongu_tarihi).toLocaleDateString("tr-TR")
                              : "-"}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter bg="white" px={6} py={4}>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            Kapat
          </Button>
          <Button colorScheme="blue" onClick={handleSave} isLoading={saving} size="sm">
            Toplu Yenile
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <AlertDialog
      isOpen={simAskOpen}
      leastDestructiveRef={simCancelRef}
      onClose={() => setSimAskOpen(false)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="md" fontWeight="bold">
            Seçili araçlarda boşa çıkan SIM
          </AlertDialogHeader>
          <AlertDialogBody fontSize="sm">
            Seçtiğiniz {bosaSelectedCount} araçta, abonelik süresi dolduğu için SIM numarası SIM
            yönetiminde <strong>boşa çıkan</strong> olarak işaretli. Toplu yenilemede{" "}
            <strong>aynı SIM ile devam edecekseniz</strong> <strong>Evet</strong> deyin; bu
            araçlarda eşleşen kayıtlar tekrar <strong>kullanımda</strong> yapılır. Yeni / farklı
            SIM kullanacaksanız <strong>Hayır</strong> deyin (SIM yönetimini manuel güncellersiniz).
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={simCancelRef} onClick={() => setSimAskOpen(false)} size="sm">
              İptal
            </Button>
            <Button
              size="sm"
              mr={3}
              onClick={() => {
                setSimAskOpen(false);
                runBulkRenew(false);
              }}
            >
              Hayır
            </Button>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={() => {
                setSimAskOpen(false);
                runBulkRenew(true);
              }}
            >
              Evet, eski SIM ile devam
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}


